import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import React, {
	useState,
	useRef,
	useImperativeHandle,
	forwardRef,
} from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';

import type { TranslationFunction } from '../../types/common';

dayjs.extend(duration);

interface FileProgressHelper {
	[index: string]: {
		progress: number;
		controller?: AbortController;
		status: string;
		startAt: number;
		cancelled: boolean;
	};
}

interface ModalHelper {
	fileProgressHelper: FileProgressHelper;
	files: Array<{ size: number }>;
}

interface UploadModalFooterProps {
	t: TranslationFunction;
	cancelAllFileUpload: () => void;
	modalHelper: React.MutableRefObject<ModalHelper>;
	showCancelButton?: boolean;
}

export interface UploadModalFooterHandle {
	setProcessing: () => void;
	setTime: (val: number, index: string, loaded?: number, total?: number) => void;
}

const UploadModalFooter = forwardRef<UploadModalFooterHandle, UploadModalFooterProps>(
	({ t, cancelAllFileUpload, modalHelper, showCancelButton = false }, ref) => {
		const [fileProcessing, setProcessing] = useState<boolean>(false);
		const [modalHasFiles, setModalHasFiles] = useState<boolean>(false);
		const [fileProcessingTime, setFileProcessingTime] = useState<Record<string, number>>({});
		const durationElementRef = useRef<{ duration: string } | null>(null);
		// Track the upload data for more stable calculations
		const uploadDataRef = useRef<{
			totalSize: number;
			loadedSize: number;
			startTime: number | null;
			lastUpdateTime: number | null;
			uploadSpeed: number;
			hasPassedInitialDelay: boolean;
			loadedByFile?: Record<string, number>;
		}>({
			totalSize: 0,
			loadedSize: 0,
			startTime: null,
			lastUpdateTime: null,
			uploadSpeed: 0,
			hasPassedInitialDelay: false,
		});

		useImperativeHandle(ref, () => {
			return {
				setProcessing() {
					const AllFiles = Object.keys(modalHelper.current.fileProgressHelper);
					const processingFiles = Object.keys(
						modalHelper.current.fileProgressHelper,
					).filter(
						(key) =>
							modalHelper.current.fileProgressHelper[key].status === 'progress',
					);

					// Check for files with error status
					/* const errorFiles = Object.keys(
						modalHelper.current.fileProgressHelper,
					).filter((key) =>
						['error', 'invalid'].includes(modalHelper.current.fileProgressHelper[key].status),
					); */

					// Initialize upload tracking data when files start processing
					if (processingFiles.length > 0 && !uploadDataRef.current.startTime) {
						uploadDataRef.current.startTime = Date.now();
						uploadDataRef.current.lastUpdateTime = Date.now();
						uploadDataRef.current.hasPassedInitialDelay = false;
						durationElementRef.current = {
							duration: t('startingUpload'),
						};
					}

					// Clear duration when all files are done processing
					if (processingFiles.length === 0 && durationElementRef.current) {
						durationElementRef.current = null;
						uploadDataRef.current = {
							totalSize: 0,
							loadedSize: 0,
							startTime: null,
							lastUpdateTime: null,
							uploadSpeed: 0,
							hasPassedInitialDelay: false,
						};
					}

					setProcessing(processingFiles.length > 0);
					setModalHasFiles(AllFiles.length > 0);
				},
				setTime(val, index, loaded, total) {
					const timeObject = { ...fileProcessingTime };
					timeObject[index] = val;
					setFileProcessingTime(timeObject);

					// Update the upload data tracking
					if (loaded !== undefined && total !== undefined) {
						const currentTime = Date.now();
						const elapsedMs = currentTime - uploadDataRef.current.startTime;
						const INITIAL_DELAY_MS = 3000;

						if (!uploadDataRef.current.hasPassedInitialDelay && elapsedMs < INITIAL_DELAY_MS) {
							durationElementRef.current = {
								duration: t('startingUpload'),
							};
							return;
						}

						if (!uploadDataRef.current.hasPassedInitialDelay && elapsedMs >= INITIAL_DELAY_MS) {
							uploadDataRef.current.hasPassedInitialDelay = true;
						}

						// Update total and loaded sizes
						if (!uploadDataRef.current.totalSize) {
							// First time setting sizes, initialize
							uploadDataRef.current.totalSize = 0;
							uploadDataRef.current.loadedSize = 0;

							// Sum up total sizes from all files
							Object.keys(modalHelper.current.fileProgressHelper).forEach(
								(fileIndex) => {
									if (modalHelper.current.files[fileIndex]) {
										uploadDataRef.current.totalSize +=
											modalHelper.current.files[fileIndex].size;
									}
								},
							);
						}

						// Initialize loadedByFile if needed
						if (!uploadDataRef.current.loadedByFile) {
							uploadDataRef.current.loadedByFile = {};
						}

						// Store current loaded amount for this file
						uploadDataRef.current.loadedByFile[index] = loaded;

						// Recalculate total loaded across all files
						uploadDataRef.current.loadedSize = Object.values(
							uploadDataRef.current.loadedByFile,
						).reduce((sum, fileLoaded) => sum + fileLoaded, 0);

						// Calculate overall progress
						if (elapsedMs > 0) {
							// Calculate speed with a moving average (smoother updates)
							const timeSinceLastUpdate =
								currentTime - uploadDataRef.current.lastUpdateTime;
							if (timeSinceLastUpdate > 500) {
								// Only update speed every 500ms
								const instantSpeed =
									uploadDataRef.current.loadedSize / elapsedMs;
								// Use weighted average to smooth speed calculation (70% previous, 30% new)
								uploadDataRef.current.uploadSpeed = uploadDataRef.current
									.uploadSpeed
									? 0.7 * uploadDataRef.current.uploadSpeed + 0.3 * instantSpeed
									: instantSpeed;
								uploadDataRef.current.lastUpdateTime = currentTime;
							}
						}

						// Calculate time remaining based on consistent speed
						if (uploadDataRef.current.uploadSpeed > 0) {
							const remainingBytes =
								uploadDataRef.current.totalSize -
								uploadDataRef.current.loadedSize;
							const remainingTimeMs =
								remainingBytes / uploadDataRef.current.uploadSpeed;

							// Create duration object
							const remainingDuration = dayjs.duration(remainingTimeMs);
							const hours = remainingDuration.hours();
							const minutes = remainingDuration.minutes();

							// Format duration text
							let durationText = '';

							if (hours > 0) {
								durationText = `${hours} ${t('hr')}`;
							}

							if (minutes > 0) {
								durationText =
									`${durationText} ${minutes} ${t('minLeft')}`.trim();
							} else if (hours === 0) {
								// If less than a minute and no hours
								durationText = t('lessThanMinLeft');
							}

							durationElementRef.current = {
								duration: `${durationText}...`,
							};

							// Check if upload is finishing (when remaining time is very short or progress is near completion)
							const progressPercentage = (uploadDataRef.current.loadedSize / uploadDataRef.current.totalSize) * 100;
							if (progressPercentage >= 99) {
								durationElementRef.current = {
									duration: t('finishingUpload'),
								};
							}
						}
					}
				},
			};
		}, []);

		return (
			<div ref={ref}>
				{modalHasFiles && (
					<Modal.Footer
						className="d-flex justify-content-between"
						style={{ minHeight: 60 }}
					>
						<div className="text-400">
							{fileProcessing &&
								durationElementRef.current &&
								`${durationElementRef.current.duration}`}
						</div>
						{showCancelButton && (
							<Button
								size="sm"
								variant="link"
								onClick={() => {
									cancelAllFileUpload();
								}}
							>
								Cancel All
							</Button>
						)}
					</Modal.Footer>
				)}
			</div>
		);
	},
);

UploadModalFooter.displayName = 'UploadModalFooter';

export default UploadModalFooter;
